let Pagination = {};


Pagination = (function () {
  function normalizePositiveInt(value, fallback) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    const normalized = Math.floor(parsed);
    return normalized > 0 ? normalized : fallback;
  }

  function getTotalPages(totalItems, pageSize) {
    return Math.max(1, Math.ceil(totalItems / pageSize));
  }

  function clampPage(page, totalPages) {
    return Math.min(Math.max(normalizePositiveInt(page, 1), 1), totalPages);
  }

  function paginate(items, pageSize = 10, page = 1) {
    const source = Array.isArray(items) ? items : [];
    const normalizedPageSize = normalizePositiveInt(pageSize, 10);
    const totalItems = source.length;
    const totalPages = getTotalPages(totalItems, normalizedPageSize);
    const normalizedPage = clampPage(page, totalPages);
    const startIndex = (normalizedPage - 1) * normalizedPageSize;
    const endIndex = startIndex + normalizedPageSize;

    return {
      page: normalizedPage,
      pageSize: normalizedPageSize,
      totalItems,
      totalPages,
      startIndex,
      endIndex: Math.min(endIndex, totalItems),
      pageItems: source.slice(startIndex, endIndex),
      hasPrev: normalizedPage > 1,
      hasNext: normalizedPage < totalPages
    };
  }

  /**
   * Likely-next page prefetch (docs/asset-loading.md law 3): holds one
   * in-flight response keyed by the exact request it answers, plus a deferred
   * scheduler for "warm page N+1 while N is read". Best-effort — a key miss or
   * failed fetch falls back to the caller's normal fetch path.
   *
   * requestKey(page) → cache key · fetchPage(page) → Promise<pagination> ·
   * warmEntries(items) → optional low-priority media warm.
   */
  function createPagePrefetch({ requestKey, fetchPage, warmEntries }) {
    let cached = null;
    let timer = null;

    function cancel() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function start(page) {
      const key = requestKey(page);
      if (cached?.key === key) return;
      const promise = Promise.resolve(fetchPage(page)).catch(() => null);
      cached = { key, promise };
      promise.then((result) => {
        if (!result || cached?.key !== key) return;
        warmEntries?.(Array.isArray(result.items) ? result.items : []);
      });
    }

    function schedule(pagination) {
      cancel();
      const page = Number(pagination?.page) || 0;
      const totalPages = Number(pagination?.totalPages) || 0;
      if (!page || page >= totalPages) return;
      timer = setTimeout(() => {
        timer = null;
        start(page + 1);
      }, 0);
    }

    function take(page) {
      if (cached?.key !== requestKey(page)) return null;
      const { promise } = cached;
      cached = null;
      return promise;
    }

    function invalidate() {
      cached = null;
      cancel();
    }

    return { start, schedule, take, cancel, invalidate };
  }

  return {
    paginate,
    createPagePrefetch,
  };
})();

export { Pagination };
